import React from 'react';
import LandingDemoConversation from './LandingDemoConversation';

const PANEL_REVEAL_DELAY_MS = 1050;

const DEFAULT_DEMO_PANEL_STATES = {
  intro: {
    coding: '...',
    codingClassName: 'anonymous-landing__demo-chip--q',
    feedback: 'N/A',
    feedbackPending: false,
    metrics: [
      ['R:Q Ratio', '0.00'],
      ['% Change Talk', '0.0%'],
      ['% MI Consistent', '0.0%'],
      ['Motivation Slope', 'N/A'],
      ['Motivation Correlation', 'N/A'],
    ],
  },
  reflection: {
    coding: 'CR',
    codingClassName: 'anonymous-landing__demo-chip--cr',
    feedback: "Strong complex reflection. It captures both sides of the client's ambivalence without pushing too quickly toward action.",
    feedbackPending: true,
    metrics: [
      ['R:Q Ratio', '1.00'],
      ['% Change Talk', '41.7%'],
      ['% MI Consistent', '100.0%'],
      ['Motivation Slope', '+0.14'],
      ['Motivation Correlation', '0.58'],
    ],
  },
  question: {
    coding: 'Q',
    codingClassName: 'anonymous-landing__demo-chip--q',
    feedback: 'Open-ended question follows the reflection well. It invites the client to elaborate on readiness instead of defending the status quo.',
    feedbackPending: true,
    metrics: [
      ['R:Q Ratio', '0.50'],
      ['% Change Talk', '63.2%'],
      ['% MI Consistent', '100.0%'],
      ['Motivation Slope', '+0.27'],
      ['Motivation Correlation', '0.71'],
    ],
  },
};

const DEFAULT_PANEL_REVEAL_BY_TURN = {
  1: 'reflection',
  3: 'question',
};

export default function LandingDemoShowcase({
  turns,
  panelStates = DEFAULT_DEMO_PANEL_STATES,
  panelRevealByTurn = DEFAULT_PANEL_REVEAL_BY_TURN,
  showPredictedCoding = true,
  showMetrics = true,
  className = '',
  hideMotivationCorrelation = false,
}) {
  const [visiblePanelState, setVisiblePanelState] = React.useState(panelStates.intro);
  const [panelVersion, setPanelVersion] = React.useState(0);
  const pendingTimeoutRef = React.useRef(null);

  const queuePanelReveal = React.useCallback((nextState) => {
    if (pendingTimeoutRef.current) {
      window.clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }

    return new Promise((resolve) => {
      pendingTimeoutRef.current = window.setTimeout(() => {
        setVisiblePanelState(nextState);
        setPanelVersion((current) => current + 1);
        pendingTimeoutRef.current = null;
        resolve();
      }, PANEL_REVEAL_DELAY_MS);
    });
  }, []);

  const handleClinicianTurnShown = React.useCallback((turnIndex) => {
    const nextStateKey = panelRevealByTurn[turnIndex];
    if (nextStateKey && panelStates[nextStateKey]) {
      return queuePanelReveal(panelStates[nextStateKey]);
    }

    return Promise.resolve();
  }, [panelRevealByTurn, panelStates, queuePanelReveal]);

  const handleLoopReset = React.useCallback(() => {
    if (pendingTimeoutRef.current) {
      window.clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    setVisiblePanelState(panelStates.intro);
    setPanelVersion(0);
  }, [panelStates]);

  React.useEffect(() => {
    setVisiblePanelState(panelStates.intro);
    setPanelVersion(0);
  }, [panelStates]);

  React.useEffect(() => () => {
    if (pendingTimeoutRef.current) {
      window.clearTimeout(pendingTimeoutRef.current);
    }
  }, []);

  return (
    <div className={`anonymous-landing__demo${className ? ` ${className}` : ''}`}>
      <LandingDemoConversation
        onClinicianTurnShown={handleClinicianTurnShown}
        onLoopReset={handleLoopReset}
        turns={turns}
        avatarMode="feedback"
      />
      <div className="anonymous-landing__demo-panels">
        {showPredictedCoding && (
          <div className="anonymous-landing__demo-card">
            <p className="anonymous-landing__demo-card-title">AutoMITI</p>
            <div key={`automiti-${panelVersion}-${visiblePanelState.coding}`} className="anonymous-landing__demo-automiti anonymous-landing__demo-panel-content--animated">
              <span className="anonymous-landing__demo-automiti-label">Predicted Coding</span>
              <span className={`anonymous-landing__demo-chip ${visiblePanelState.codingClassName}`}>{visiblePanelState.coding}</span>
            </div>
          </div>
        )}
        <div className="anonymous-landing__demo-card">
          <p className="anonymous-landing__demo-card-title">Feedback</p>
          {visiblePanelState.feedback === 'N/A' ? (
            <div key={`feedback-${panelVersion}-na`} className="anonymous-landing__demo-feedback-na anonymous-landing__demo-panel-content--animated">N/A</div>
          ) : (
            <p key={`feedback-${panelVersion}-${visiblePanelState.coding}`} className="anonymous-landing__demo-feedback-text anonymous-landing__demo-panel-content--animated">
              {visiblePanelState.feedback}
            </p>
          )}
        </div>
        {showMetrics && (
          <div className="anonymous-landing__demo-card anonymous-landing__demo-card--metrics">
            <p className="anonymous-landing__demo-card-title">In Session Metrics</p>
            <div key={`metrics-${panelVersion}-${visiblePanelState.coding}`} className="anonymous-landing__demo-scores-grid anonymous-landing__demo-panel-content--animated">
              {visiblePanelState.metrics
                .filter(([label]) => !(hideMotivationCorrelation && label === 'Motivation Correlation'))
                .map(([label, value]) => (
                <div key={label} className="anonymous-landing__demo-score-item">
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
